/**
 * useFavoritos — gerencia lista de pageIds favoritos do usuario (Bloco UI / UI.5).
 *
 * Persiste em localStorage `pharuerp:favoritos` (array de strings).
 * Try/catch defensivo (pattern SS3.B.5) para nao quebrar se quota cheia
 * ou localStorage indisponivel.
 *
 * API:
 *   const { favoritos, toggle, isFavorito } = useFavoritos();
 *
 * - `favoritos`: Set<string> de pageIds
 * - `toggle(pageId)`: adiciona se nao tem, remove se ja tem
 * - `isFavorito(pageId)`: boolean
 *
 * Decisao: localStorage (nao backend) por enquanto. Quando aparecer demanda
 * de "favoritos cross-device", migra pra `PreferenciaDashboard` ou model novo.
 * Hoje funciona offline e nao adiciona round trip pos-login.
 */

import { useCallback, useEffect, useState } from "react";
import { getItemTenant, setItemTenant } from "../utils/tenantStorage";

const STORAGE_KEY = "pharuerp:favoritos";


function _ler() {
  try {
    const raw = getItemTenant(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}


function _escrever(lista) {
  try {
    setItemTenant(STORAGE_KEY, JSON.stringify(lista));
  } catch {
    // Quota cheia ou storage indisponivel — degrada silenciosamente,
    // mantendo state em memoria. Proxima sessao volta vazia.
  }
}


export default function useFavoritos() {
  const [favoritos, setFavoritos] = useState(() => new Set(_ler()));

  // Persiste sempre que muda
  useEffect(() => {
    _escrever(Array.from(favoritos));
  }, [favoritos]);

  const toggle = useCallback((pageId) => {
    if (!pageId) return;
    setFavoritos((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) {
        next.delete(pageId);
      } else {
        next.add(pageId);
      }
      return next;
    });
  }, []);

  const isFavorito = useCallback(
    (pageId) => favoritos.has(pageId),
    [favoritos],
  );

  return { favoritos, toggle, isFavorito };
}
